import {FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import {ChemicalStructureSearchOperator, Operator, ScoresById} from 'search/SearchConstants';

export enum ChemDrawStoichiometryResult {
  NO_TABLE = 'NO_TABLE',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

interface ChemDrawNoTableStoichiometryData {
  result: ChemDrawStoichiometryResult.NO_TABLE;
}
interface ChemDrawSuccessStoichiometryData {
  result: ChemDrawStoichiometryResult.SUCCESS;
  reactantsHeader: Array<string>;
  reactantsTable: Array<Array<string>>;
  productsHeader: Array<string>;
  productsTable: Array<Array<string>>;
}
interface ChemDrawErrorStoichiometryData {
  result: ChemDrawStoichiometryResult.ERROR;
  message: string;
}

export type ChemDrawStoichiometryData =
  | ChemDrawNoTableStoichiometryData
  | ChemDrawSuccessStoichiometryData
  | ChemDrawErrorStoichiometryData;

// Keep in sync with class definition in benchling/chemistry/models/chemical_structure.py
export enum ChemicalFormatType {
  SMILES = 'smiles',
  MOLFILE = 'molfile',
}

export interface ChemicalStructureInput {
  structureFormat: ChemicalFormatType;
  value: string;
}

export type ChemicalStructureBlob = Blob & {name?: string};

export const MOLECULE_ROW_HEIGHT = 200;

export const UNRECOGNIZED_FRAGMENTS_ERROR_CODE = 'MULTIPLE_UNRECOGNIZED_FRAGMENTS';

export const EMPTY_MOLFILE =
  '\n  MJ211100                      \n\n  0  0  0  0  0  0  0  0  0  0999 V2000\nM  END\n';

export const EMPTY_MRV = '<cml><MDocument></MDocument></cml>';

export const ChemistryPreferences = {
  SHOULD_HIGHLIGHT_SUBSTRUCTURE: 'shouldHighlightSubstructurePref',
  MIN_SIMILARITY: 'minSimilarityPref',
  CHEM_SEARCH_TYPE: 'chemSearchTypePref',
};

export enum ChemicalProperty {
  ATOM_COUNT = 'ATOM_COUNT',
  EXACT_MOLECULAR_WEIGHT = 'EXACT_MOLECULAR_WEIGHT',
  EXACT_FORMULA_WEIGHT = 'EXACT_FORMULA_WEIGHT',
  FORMULA_WEIGHT = 'FORMULA_WEIGHT',
  FRAGMENT_COUNT = 'FRAGMENT_COUNT',
  HEAVY_ATOM_COUNT = 'HEAVY_ATOM_COUNT',
  HYDROGEN_ACCEPTOR_COUNT = 'HYDROGEN_ACCEPTOR_COUNT',
  HYDROGEN_DONOR_COUNT = 'HYDROGEN_DONOR_COUNT',
  LOGP = 'LOGP',
  MOLAR_REFRACTIVITY = 'MOLAR_REFRACTIVITY',
  MOLECULAR_WEIGHT = 'MOLECULAR_WEIGHT',
  NUM_AROMATIC_RINGS = 'NUM_AROMATIC_RINGS',
  RING_COUNT = 'RING_COUNT',
  ROTATABLE_BOND_COUNT = 'ROTATABLE_BOND_COUNT',
  SP3_ATOM_COUNT = 'SP3_ATOM_COUNT',
  TOPOLOGICAL_POLAR_SURFACE_AREA = 'TOPOLOGICAL_POLAR_SURFACE_AREA',
}

export const CHEMICAL_PROPERTY_TO_DISPLAY_NAMES: {[key in ChemicalProperty]: string} = {
  [ChemicalProperty.MOLECULAR_WEIGHT]: 'MW',
  [ChemicalProperty.EXACT_MOLECULAR_WEIGHT]: 'Exact MW',
  [ChemicalProperty.FORMULA_WEIGHT]: 'FW',
  [ChemicalProperty.EXACT_FORMULA_WEIGHT]: 'Exact FW',
  [ChemicalProperty.ATOM_COUNT]: 'Atom count',
  [ChemicalProperty.HEAVY_ATOM_COUNT]: 'Heavy atom count',
  [ChemicalProperty.SP3_ATOM_COUNT]: 'sp3 atom count',
  [ChemicalProperty.HYDROGEN_ACCEPTOR_COUNT]: 'HBA',
  [ChemicalProperty.HYDROGEN_DONOR_COUNT]: 'HBD',
  [ChemicalProperty.RING_COUNT]: 'Ring count',
  [ChemicalProperty.NUM_AROMATIC_RINGS]: 'Aromatic ring count',
  [ChemicalProperty.ROTATABLE_BOND_COUNT]: 'Rotatable bond count',
  [ChemicalProperty.LOGP]: 'cLogP',
  [ChemicalProperty.TOPOLOGICAL_POLAR_SURFACE_AREA]: 'TPSA',
  [ChemicalProperty.MOLAR_REFRACTIVITY]: 'Molar refractivity',
  [ChemicalProperty.FRAGMENT_COUNT]: 'Fragment count',
};

export interface ChemSearchFolderItemListingExtras {
  substructureQuery?: string | null;
  shouldHighlightSubstructure?: boolean;
  searchScoresById?: ScoresById;
}

export const SIMILARITY_SCORE_CUTOFFS = [0.99, 0.95, 0.9, 0.8, 0.7, 0.5];

export const CHEM_SEARCH_OPERATOR_TO_DISPLAY_NAME: {[key in ChemicalStructureSearchOperator]: string} = {
  [Operator.SIMILARITY]: 'Similarity',
  [Operator.SUBSTRUCTURE]: 'Substructure',
};

export const DEFAULT_MIN_SIMILARITY_CUTOFF = 0.8;

export const ANY_REACTION_COMPONENT_ROLE_LABEL = 'Any role';

export const MOLECULE_CHEM_SEARCH_MODAL_TITLE = 'Chemical structure molecule search';
export const REACTION_CHEM_SEARCH_MODAL_TITLE = 'Chemical structure reaction search';

// TODO(BNCH-158512): Add multi-molecule clause to reaction chem search description.
export const REACTION_CHEM_SEARCH_DESCRIPTION =
  'Search for a reaction by drawing the chemical structure of a molecule present in that reaction. You can refine your search by indicating the reaction role.';

// Benchling enum for specifying chemical file formats.
export enum ChemicalFileFormat {
  INCHI = 'inchi',
  MOL = 'mol', // Do not distinguish between v2 and v3 molfiles.
  SMILES = 'smiles',
  SDF = 'sdf',
  CDX = 'cdx',
  MRV = 'mrv',
  RDF = 'rdf',
  RXN = 'rxn',
}

// Note that these values are expected to correspond to the literals in
// benchling/external_files/previews.py DocumentTypeLiteral since they are compared in
// client/notebook/notes/attachments/NoteLinkedFilePreview.js as the "documentType"
export const CHEMICAL_FILE_FORMAT_TO_EXTENSION: Record<ChemicalFileFormat, string> = {
  [ChemicalFileFormat.INCHI]: 'inchi',
  [ChemicalFileFormat.MOL]: 'mol',
  [ChemicalFileFormat.SMILES]: 'smiles',
  [ChemicalFileFormat.SDF]: 'sdf',
  [ChemicalFileFormat.CDX]: 'cdx',
  [ChemicalFileFormat.MRV]: 'mrv',
  [ChemicalFileFormat.RDF]: 'rdf',
  [ChemicalFileFormat.RXN]: 'rxn',
};

// Formats that can only contain a single molecule.
export const MOLECULE_FORMATS = [ChemicalFileFormat.INCHI, ChemicalFileFormat.MOL, ChemicalFileFormat.SMILES];
// Formats that may specify a reaction, omitting cdxml since it's not well supported.
export const REACTION_FORMATS = [
  ChemicalFileFormat.CDX,
  ChemicalFileFormat.MRV,
  ChemicalFileFormat.RDF,
  ChemicalFileFormat.RXN,
];

export const CHEMICAL_FORMATS = [...REACTION_FORMATS, ...MOLECULE_FORMATS];

export const REACTION_EXTENSIONS = REACTION_FORMATS.map(
  (format) => CHEMICAL_FILE_FORMAT_TO_EXTENSION[format]
);
export const CHEMICAL_EXTENSIONS = CHEMICAL_FORMATS.map(
  (format) => CHEMICAL_FILE_FORMAT_TO_EXTENSION[format]
);

export const EXPORT_DISPLAY_NAME: Record<ChemicalFileFormat, string> = {
  [ChemicalFileFormat.MRV]: `MRV (.${CHEMICAL_FILE_FORMAT_TO_EXTENSION[ChemicalFileFormat.MRV]})`,
  [ChemicalFileFormat.SMILES]: `SMILES (.${CHEMICAL_FILE_FORMAT_TO_EXTENSION[ChemicalFileFormat.SMILES]})`,
  [ChemicalFileFormat.MOL]: `Molfile (.${CHEMICAL_FILE_FORMAT_TO_EXTENSION[ChemicalFileFormat.MOL]})`,
  [ChemicalFileFormat.SDF]: `SDF (.${CHEMICAL_FILE_FORMAT_TO_EXTENSION[ChemicalFileFormat.SDF]})`,
  [ChemicalFileFormat.INCHI]: `InChI (.${CHEMICAL_FILE_FORMAT_TO_EXTENSION[ChemicalFileFormat.INCHI]})`,
  [ChemicalFileFormat.CDX]: `CDX (.${CHEMICAL_FILE_FORMAT_TO_EXTENSION[ChemicalFileFormat.CDX]})`,
  [ChemicalFileFormat.RDF]: `RDF (.${CHEMICAL_FILE_FORMAT_TO_EXTENSION[ChemicalFileFormat.RDF]})`,
  [ChemicalFileFormat.RXN]: `RXN (.${CHEMICAL_FILE_FORMAT_TO_EXTENSION[ChemicalFileFormat.RXN]})`,
};

// Keep in sync with ReactionFormat in services/chemistry/openapi.yaml; this is a one-off format used for
// the stoichiometry table currently.
export enum ReactionFormat {
  RXN_V3 = 'rxn:V3',
}

export interface ReactionComponent {
  moleculeId: string | null;
  name: string | null;
  canonicalSmiles: string;
  // Optional only for backward compatibility with existing rows; new parsed components should include it.
  drawnMolfile?: string;
}

export interface ParseReactionComponentsResponse {
  noteLinkedFile: FromServerNoteLinkedFile;
  reactants: Array<ReactionComponent>;
  products: Array<ReactionComponent>;
}
